/**
 * State Style05 - Lux card (Illuminance)
 */
import type { StateControlProps, StateLifecycle } from './stateStyleTypes'
import {
  formatStateValue,
  getStateConfig,
  getStateLifecycleColor,
  inferStateLifecycle,
} from './traitStyleConfig'
import './StateStyle05.css'

const LIFECYCLE_LABEL: Record<StateLifecycle, string> = {
  live: 'LIVE',
  warn: 'WARN',
  error: 'ERROR',
  stale: 'STALE',
  offline: 'OFFLINE',
}

function toNumber(value: unknown): number | null {
  if (typeof value === 'number' && Number.isFinite(value)) return value
  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : null
}

export function StateStyle05({
  stateKey,
  value,
  unit = '',
  label,
  icon,
  lifecycle,
  updatedAtLabel,
  precision,
  thresholdEnabled,
  disabled = false,
}: StateControlProps) {
  const config = getStateConfig(stateKey, { icon })
  const displayValue = formatStateValue(value, precision)
  const displayLabel = label ?? config.label ?? stateKey
  const resolvedLifecycle =
    lifecycle ??
    (disabled
      ? 'offline'
      : inferStateLifecycle(stateKey, value, {
          thresholdEnabled,
        }))
  const lifecycleColor = getStateLifecycleColor(resolvedLifecycle)
  const updatedLabel = String(updatedAtLabel || '2m 前更新')
  const n = toNumber(value)
  const meterPercent = n == null ? 0 : Math.max(0, Math.min(100, (n / 1000) * 100))
  const badgeText = LIFECYCLE_LABEL[resolvedLifecycle]

  return (
    <div
      className="trait-state-style05"
      data-css-slot="stateContainer"
      data-css-slot-label="State Container"
      data-css-state={resolvedLifecycle}
    >
      <div className="trait-state-style05__head">
        <div className="trait-state-style05__title-wrap">
          <span
            className="material-symbols-outlined trait-state-style05__icon"
            data-css-slot="stateIcon"
            data-css-slot-label="State Icon"
            data-css-state={resolvedLifecycle}
            style={{ color: config.color }}
          >
            {config.icon}
          </span>
          <div>
            <div
              className="trait-state-style05__label"
              data-css-slot="stateLabel"
              data-css-slot-label="State Label"
              data-css-state={resolvedLifecycle}
            >
              {displayLabel}
            </div>
            <div className="trait-state-style05__subtitle">Ambient Light</div>
          </div>
        </div>
        <span
          className="trait-state-style05__badge"
          data-css-slot="stateBadge"
          data-css-slot-label="State Lifecycle Badge"
          data-css-state={resolvedLifecycle}
          style={{ '--state-lifecycle-color': lifecycleColor } as React.CSSProperties}
        >
          {badgeText}
        </span>
      </div>

      <div className="trait-state-style05__value-wrap">
        <span
          className="trait-state-style05__value"
          data-css-slot="stateValue"
          data-css-slot-label="State Value"
          data-css-state={resolvedLifecycle}
        >
          {displayValue}
        </span>
        <span
          className="trait-state-style05__unit"
          data-css-slot="stateUnit"
          data-css-slot-label="State Unit"
          data-css-state={resolvedLifecycle}
        >
          {unit || 'Lux'}
        </span>
      </div>

      <div className="trait-state-style05__meter-axis">
        <span>Dark</span>
        <span>Bright</span>
      </div>
      <div
        className="trait-state-style05__meter"
        data-css-slot="stateMeter"
        data-css-slot-label="State Meter"
        data-css-state={resolvedLifecycle}
      >
        <div style={{ width: `${meterPercent}%` }} />
      </div>

      <div
        className="trait-state-style05__updated"
        data-css-slot="stateUpdatedAt"
        data-css-slot-label="State Updated Time"
        data-css-state={resolvedLifecycle}
      >
        <span className="material-symbols-outlined">schedule</span>
        {updatedLabel}
      </div>
    </div>
  )
}
